import { P2GAlert, P2GBox, P2GGrid, P2GButton, P2GLink, P2GProgress, P2GText } from '@parcel2go/core-ui';
import { useState } from 'react';
import { ProviderGroup } from '../../../../lib/helpers/SmartSendBulkActionApplier';
import DebouncedInput from '../../../DebouncedInput';
import { CanUseDiscount } from './CanUseDiscount';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';

interface props {
  group: ProviderGroup;
  updateDiscountCode: (discountCode: string) => void;
}

const ProviderGroupPromoCode: React.FC<props> = (props) => {
  const canUseDiscount = CanUseDiscount(props.group.SelectedPaymentMethod);
  const [code, setCode] = useState(props.group.Discount?.DiscountCode);
  const [show, setShow] = useState(!!code);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const onSave = () => {
    setSaving(true);
    setShow(true);
    props.updateDiscountCode(code);
  };

  const onReset = () => {
    setSaving(false);
    setCode(null);
    setShow(false);
    props.updateDiscountCode(null);
  };

  const loading = props.group?.Discount?.Loading;

  if (!canUseDiscount) {
    return null;
  }

  if (!show) {
    return (
      <P2GBox mt={2}>
        <P2GLink variant="body2" onClick={() => setShow(true)}>
          {t('USE_PROMO_CODE_BUTTON', { defaultValue: 'Use Promo Code' })}
        </P2GLink>
      </P2GBox>
    );
  }

  if (props.group.Discount?.Applied) {
    return (
      <P2GBox mt={2}>
        <P2GAlert palette={props.group.Discount.TotalDiscount > 0 ? 'success' : 'info'}>
          <P2GGrid container spacing={1} alignItems="center">
            <P2GGrid item flexGrow={1}>
              {props.group.Discount.TotalDiscount > 0 ? (
                <P2GText variant="body2">
                  <Trans
                    i18nKey="PROMO_CODE_SUCCESS"
                    t={t}
                    defaults="Congratulations! Your code <strong>{{code}}</strong> is saving you up to {{discount}}!"
                    values={{
                      code: code.toUpperCase(),
                      discount: toDisplayCurrency(props.group.CurrencyCode, props.group.Discount.TotalDiscount)
                    }}
                  />
                </P2GText>
              ) : (
                <P2GText variant="body2" colour="error">
                  {t('INVALID_PROMO_CODE_ERROR', { defaultValue: 'Sorry, this promotional code is not valid.' })}
                </P2GText>
              )}
            </P2GGrid>
            <P2GGrid item flexShrink={1}>
              <P2GLink variant="body2" onClick={onReset}>
                {t('RESET_BUTTON', { defaultValue: 'Reset' })}
              </P2GLink>
            </P2GGrid>
          </P2GGrid>
        </P2GAlert>
      </P2GBox>
    );
  }

  return (
    <P2GBox mt={2}>
      <P2GGrid container spacing={1} alignItems="center">
        <P2GGrid item flexGrow={1}>
          <DebouncedInput
            value={code}
            disabled={saving || loading}
            onChange={setCode}
            label={t('PROMOTION_CODE_LABEL', { defaultValue: 'Promotion Code' })}
            id="Promotion Code"
          />
        </P2GGrid>
        {code && (
          <P2GGrid item flexShrink={1}>
            {loading || saving ? (
              <P2GProgress />
            ) : (
              <P2GButton variant="primary" onClick={onSave}>
                {t('APPLY_PROMO_CODE_BUTTON', { defaultValue: 'Apply Code' })}
              </P2GButton>
            )}
          </P2GGrid>
        )}
      </P2GGrid>
    </P2GBox>
  );
};

export default ProviderGroupPromoCode;
